import styles from "./analytics.module.scss";
import { useAppDispatch, useAppSelector } from "../store";
import { Bar, Pie, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useState } from "react";
import { fetchAnalyticsAction } from "./analyticsSlice";
import KeyInfo from "src/components/UI/keyInfo/KeyInfo";
import { barOptions, keyInfoConfig, lineOptions, pieOptions, userAssetConfig } from "./AnalyticsConfig";
import { fetchOrganisationSubscriptionAction } from "../auth/loginSlice";
import TransactionWelCard from "src/components/transactionWelCard/TransactionWelCard";
import { Button, ButtonGroup, Skeleton } from "@mui/material";
import { dateTimeFormat } from "src/utils/utils";
import moment from "moment";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  ChartDataLabels
);

const generateColors = (numColors: number) => {
  const colors = [];
  const saturation = 70;
  const lightness = 50;
  const opacity = 0.6;

  const goldenAngle = 137.5;
  let hue = 0;

  for (let i = 0; i < numColors; i++) {
    colors.push({
      backgroundColor: `hsla(${hue}, ${saturation}%, ${lightness}%, ${opacity})`,
      borderColor: `hsla(${hue}, ${saturation}%, ${lightness}%)`
    });

    hue = (hue + goldenAngle) % 360;
  }
  return colors;
};

export default function Analytics() {
  const { analytics, loading } = useAppSelector((state) => state.analytics);
  const { cognitoConfig, OrgSubscription, userInfo } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [selectedDays, setSelectedDays] = useState(28);

  const relevantData = [...(analytics?.data || [])].sort((a, b) =>
    moment(b.report_date).diff(moment(a.report_date))
  )[0];

  useEffect(() => {
    const filter: any = {
      report_content_filter: {
        // object_ids: ["225d47fc-ee21-455f-a7f3-ee1d5f0b5cfe"]
        object_ids: [cognitoConfig?.currentOrganisation?.organisation_id],
        report_date: {
          min: moment()
            .subtract(selectedDays + 1, "days")
            .startOf("day")
            .format("YYYY-MM-DD"),
          max: moment().subtract(1, "days").endOf("day").format("YYYY-MM-DD")
        }
      }
    };

    const data = {
      size: selectedDays,
      from: 0,
      filter
    };

    dispatch(fetchAnalyticsAction(data, selectedDays));
    dispatch(fetchOrganisationSubscriptionAction(cognitoConfig?.organisationSubscriptions ?? []));
  }, [
    cognitoConfig?.currentOrganisation?.organisation_id,
    cognitoConfig?.organisationSubscriptions,
    dispatch,
    selectedDays
  ]);

  const barChartData = {
    labels: analytics?.barChart?.weeks,
    datasets: [
      {
        label: "Transactions",
        data: analytics?.barChart?.totalTransactions,
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
        borderRadius: 10
      },
      {
        label: "Deposits",
        data: analytics?.barChart?.totalDeposits,
        backgroundColor: "rgba(255, 99, 132, 0.6)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
        borderRadius: 10
      }
    ]
  };

  const colors = generateColors(analytics?.pieChart?.categories.length);

  const pieData = {
    labels: analytics?.pieChart?.categories,
    datasets: [
      {
        data: analytics?.pieChart?.amounts,
        backgroundColor: colors.map((c) => c.backgroundColor),
        borderColor: colors.map((c) => c.borderColor),
        borderWidth: 1,
        hoverBorderColor: "#fff",
        hoverOffset: 8
      }
    ]
  };

  const lineData = {
    labels: analytics?.lineChart?.weeks,
    datasets: [
      {
        label: "Number of Transaction",
        data: analytics?.lineChart?.amount,
        fill: false,
        backgroundColor: "rgba(75, 192, 192, 1)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 2
      }
    ]
  };

  return (
    <>
      <div className={styles.WelCard}>
        <TransactionWelCard
          heading={"View Your Analytic Report"}
          subHeading={"View your organisation's last 30 days of analytics report"}
        />
      </div>
      <div className={styles.filterButtons}>
        <ButtonGroup
          variant="outlined"
          aria-label="analytic filter"
          style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start" }}
        >
          <Button
            onClick={() => setSelectedDays(28)}
            style={{
              backgroundColor: selectedDays === 28 ? "#1e293b" : "#fff",
              border: "1px solid #1e293b",
              color: selectedDays === 28 ? "#FFFFFF" : "#1e293b"
            }}
            aria-label="28days"
          >
            28 Days
          </Button>
          <Button
            onClick={() => setSelectedDays(60)}
            style={{
              backgroundColor: selectedDays === 60 ? "#1e293b" : "#fff",
              border: "1px solid #1e293b",
              color: selectedDays === 60 ? "#FFFFFF" : "#1e293b"
            }}
            aria-label="60days"
          >
            60 Days
          </Button>
          <Button
            onClick={() => setSelectedDays(90)}
            style={{
              backgroundColor: selectedDays === 90 ? "#1e293b" : "#fff",
              border: "1px solid #1e293b",
              color: selectedDays === 90 ? "#FFFFFF" : "#1e293b"
            }}
            aria-label="90days"
          >
            90 Days
          </Button>
        </ButtonGroup>
      </div>
      <div className={styles.chart}>
        {loading ? (
          <div className={styles.item}>
            <Skeleton variant="rectangular" className={styles.chartSkeletonLoading}></Skeleton>
            <Skeleton variant="rectangular" className={styles.chartSkeletonLoading}></Skeleton>
          </div>
        ) : (
          <>
            <div className={styles.item}>
              <div className={styles.barChart}>
                <h4>Value of Deposit and Transactions</h4>
                <Bar data={barChartData} options={barOptions} />
              </div>
              <div className={styles.lineChart}>
                <h4>Number of Transactions</h4>
                <Line data={lineData} options={lineOptions} />
              </div>
            </div>
            <div className={styles.item}>
              <div className={styles.keyInformation}>
                <h4>Key Information</h4>
                {relevantData ? (
                  <KeyInfo
                    config={keyInfoConfig}
                    data={{
                      ...relevantData,
                      planType: OrgSubscription?.list[0]?.title,
                      signUpDate: userInfo?.createdAt && dateTimeFormat(userInfo?.createdAt)?.datetime
                    }}
                  />
                ) : (
                  <p className={styles.noData}>No data available for yesterday&apos;s date.</p>
                )}
              </div>
              <div className={styles.userAsset}>
                <h4>User / Assets</h4>
                {relevantData ? (
                  <KeyInfo config={userAssetConfig} data={relevantData} />
                ) : (
                  <p className={styles.noData}>No data available for yesterday&apos;s date.</p>
                )}
              </div>
            </div>
            <div className={styles.item}>
              <div className={styles.pieChart}>
                <h4>Spend By Category</h4>
                <Pie data={pieData} options={pieOptions} />
              </div>
              <div className={styles.areaChart}>
                <h4>Receipts Uploaded %</h4>
                <p>Coming soon..</p>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
